/* eslint-disable indent, no-param-reassign,no-plusplus, no-unused-vars */
import axios from 'axios';
import dataTool from 'echarts/extension/dataTool';
import groupBy from 'lodash/groupBy';
import statsLite from 'stats-lite';
import cacheAxios from '../../util/cacheAxios';
import abbreviateNumber from '../../util/abbreviateNumber';

const financeAxios = axios.create();
const cache = new Map();
cacheAxios(financeAxios, cache);

financeAxios.interceptors.response.use(response => response,
  (error) => {
    if (error && error.response && error.response.status === 401) {
      setTimeout(() => {
        window.location.href = '/sign/in?source=/aims';
      }, 500);
    }
  });

const grid = [
  {
    // left: 110,
    right: '14%',
    bottom: '11%',
    },
  ];
const legend = {
  bottom: '10%',
  right: '1%',
  orient: 'vertical',
  align: 'right',
};

export default {
  state: {
    chartLoading: false,
    indicatorSelected: 'totalRevenue',
    companySelected: '',
    sectors: [],
    countries: [],
    relevantCompanies: [],
    relevantCompaniesIndicators: [],
    indicators: [
      {
        label: 'Total Revenue',
        code: 'totalRevenue',
      },
      {
        label: 'EBITDA',
        code: 'ebitda',
      },
      {
        label: 'Net Income',
        code: 'netIncome',
      },
      {
        label: 'Revenue Per Employee',
        code: 'revenuePerEmployee',
      },
      {
        label: 'Net Income Per Employee',
        code: 'netIncomePerEmployee',
      },
      {
        label: 'Return on Assets',
        code: 'returnOnAssets',
      },
      {
        label: 'Return on Equity',
        code: 'returnOnEquity',
      },
      {
        label: 'Return on Investment',
        code: 'returnOnInvestment',
      },
    ],
    financeChartOptions: {
      backgroundColor: '#fff',
      title: {
        text: '',
        left: 'center',
        top: '2%',
        subtextStyle: {
          fontSize: 16,
          color: '#333',
        },
        textStyle: {
          color: '#333',
        },
      },
      tooltip: {
        trigger: 'item',
      },
      xAxis: [
        {
          data: [],
          silent: false,
          splitLine: {
            show: false,
          },
        },
      ],
      yAxis: [
        {
          axisLabel: {
            textStyle: {
              color: '#999',
            },
            formatter: label => `US$${abbreviateNumber(label)}`,
          },
        },
      ],
      // legend: {
      //   data: ['Company', '20 Percentile', 'Median', '80 Percentile'],
      //   right: '2%',
      //   top: '2%',
      //   width: '10%',
      // },
      grid,
      legend,
      series: [
        {
          name: 'Company',
          type: 'scatter',
          label: {
            normal: {
              show: false,
            },
            emphasis: {
              show: false,
            },
          },
          itemStyle: {
            normal: {
              borderColor: 'rgba(0, 0, 0, 0.8)',
              borderWidth: 0,
              areaColor: '#e2e2e2',
            },
            emphasis: {
              borderColor: '#a0bfff',
              borderWidth: 2,
              areaColor: '#200fff',
            },
          },
          data: [],
          z: 3,
          tooltip: {
            formatter: (param) => {
              const tip = [
                  `${param.seriesName}: `,
                  `${param.data[0]}: US$${abbreviateNumber(param.data[1])}`,
              ].join('<br/>');
              return tip;
            },
          },
        },
        {
          name: 'box',
          type: 'boxplot',
          z: 1,
          tooltip: {
            formatter: (param) => {
              const tip = [
                  `${param.data.name}: `,
                  `upper: US$${abbreviateNumber(param.data.value[4])}`,
                  `Q3: US$${abbreviateNumber(param.data.value[3])}`,
                  `median: US$${abbreviateNumber(param.data.value[2])}`,
                  `Q1: US$${abbreviateNumber(param.data.value[1])}`,
                  `lower: US$${abbreviateNumber(param.data.value[0])}`,
              ].join('<br/>');
              return tip;
            },
          },
        },
        {
          name: 'Outliers',
          type: 'scatter',
          itemStyle: {
            normal: {
              borderColor: 'rgba(0, 0, 0, 0.3)',
              borderWidth: 1,
              areaColor: '#e2e2e2',
              opacity: 0.3,
            },
            emphasis: {
              borderColor: '#a0bfff',
              borderWidth: 2,
              areaColor: '#200fff',
            },
          },
          data: [],
          z: 2,
          symbolSize: 7,
          tooltip: {
            formatter: (param) => {
              const tip = [
                  `${param.data.company}: `,
                  `US$${abbreviateNumber(param.data.value[1])}`,
              ].join('<br/>');
              return tip;
            },
          },
        },
      ],
    },
  },
  mutations: {
    setIndicatorSelected(state, indicator) {
      state.indicatorSelected = indicator;
    },

    setCompanySelected(state, company) {
      state.companySelected = company;
    },

    setCountries(state, countries) {
      state.countries = countries;
    },

    setSectors(state, sectors) {
      state.sectors = sectors;
    },

    setChartLoading(state, value) {
      state.chartLoading = value;
    },

    setRelevantCompanies(state, companies) {
      state.relevantCompanies = companies;
    },

    setRelevantCompaniesIndicators(state, indicators) {
      state.relevantCompaniesIndicators = indicators;
    },

    plotBox(state) {
      const indicator = state.indicatorSelected;
      const extractedCompIndicators = state.relevantCompaniesIndicators
        .filter(i => i[indicator])
        .map((i) => {
          const mapped = {
            reportDate: i.reportDate.slice(0, 4),
            value: i[indicator],
            company: i.shortName,
          };
          return mapped;
        });
      const groupedCompIndicators = groupBy(extractedCompIndicators, 'reportDate');
      const keys = Object.keys(groupedCompIndicators);

      const avg = statsLite.mean(keys.map(k => groupedCompIndicators[k].length));
      const arrayChartData = keys
        .sort()
        .map((key) => {
          if (groupedCompIndicators[key].length < (avg - 1)) {
            return [];
          }

          const mapped = groupedCompIndicators[key].map(i => i.value);
          return mapped;
        });
      const data = dataTool.prepareBoxplotData(arrayChartData);
      const xAxis = { ...state.financeChartOptions.xAxis[0],
        data: data.axisData.map(i => keys[i]),
      };
      const namedBoxData = data.axisData.map((i) => {
        const out = {
          name: keys[i],
          value: data.boxData[i],
        };
        return out;
      });
      const boxSeries = { ...state.financeChartOptions.series[1],
        data: namedBoxData,
        name: indicator,
      };

      const sortedKeys = keys.sort();
      const companyDots = extractedCompIndicators.map((i) => {
        const out = {
          name: i.reportDate,
          company: i.company,
          value: [sortedKeys.indexOf(i.reportDate), i.value],
        };
        return out;
      });

      const namedOutliers = data.outliers.map((i) => {
        const out = {
          name: keys[i[0]],
          value: i,
        };
        return out;
      });
      const outlierSeries = { ...state.financeChartOptions.series[2],
        data: companyDots,
        name: indicator,
      };
      const indicatorItem = state.indicators.find(i => i.code === indicator);
      let subtext = '';
      if (indicatorItem) {
        subtext = indicatorItem.label;
      }

      const title = { ...state.financeChartOptions.title,
        subtext,
      };
      state.financeChartOptions = { ...state.financeChartOptions,
        series: [state.financeChartOptions.series[0], boxSeries, outlierSeries],
        xAxis: [xAxis],
        title,
      };
    },

    unplotBox(state) {
      const boxSeries = { ...state.financeChartOptions.series[1],
        data: [],
      };
      const outlierSeries = { ...state.financeChartOptions.series[2],
        data: [],
      };
      const title = { ...state.financeChartOptions.title,
        subtext: '',
      };
      state.financeChartOptions = { ...state.financeChartOptions,
        series: [state.financeChartOptions.series[0], boxSeries, outlierSeries],
        title,
      };
    },

    unplotCompany(state) {
      const companySeries = { ...state.financeChartOptions.series[0],
        data: [],
      };
      const title = { ...state.financeChartOptions.title,
        text: '',
      };
      state.financeChartOptions = { ...state.financeChartOptions,
        series: [companySeries, state.financeChartOptions.series[1],
          state.financeChartOptions.series[2]],
        title,
      };
    },

    plotCompany(state) {
      const company = state.companySelected;
      const indicator = state.indicatorSelected;
      const compIndicator = state.relevantCompaniesIndicators
        .filter(c => c.shortName === company);
      if (compIndicator.length === 0 || !compIndicator[0][indicator]) {
        const companySeries = { ...state.financeChartOptions.series[0],
          data: [],
        };
        state.financeChartOptions = { ...state.financeChartOptions,
          series: [companySeries, state.financeChartOptions.series[1],
            state.financeChartOptions.series[2]],
        };
        return;
      }

      const companyData = compIndicator
        .map(indicatorSet => [indicatorSet.reportDate.slice(0, 4), indicatorSet[indicator]]);
      const companySeries = { ...state.financeChartOptions.series[0],
        data: companyData,
        name: company,
      };
      const text = company;
      const title = { ...state.financeChartOptions.title,
        text,
      };
      state.financeChartOptions = { ...state.financeChartOptions,
        series: [companySeries, state.financeChartOptions.series[1],
          state.financeChartOptions.series[2]],
        title,
      };
    },
  },
  actions: {
    initFinanceData({
      commit,
    }) {
      financeAxios.get('/aims/api/finance/countries')
        .then((res) => {
          if (res.status === 200) {
            res.data.sort();
            commit('setCountries', res.data);
          }
        });
      financeAxios.get('/aims/api/finance/gics')
        .then((res) => {
          if (res.status === 200) {
            commit('setSectors', res.data);
          }
        });
    },

    getCompaniesByCountryAndIndustries({
      state,
      commit,
    }, {
      country,
      gicsCode,
    }) {
      commit('setChartLoading', true);
      commit('unplotCompany');
      gicsCode.sort();
      const query = {
        country,
        gicsCode,
      };
      financeAxios.post('/aims/api/finance/companiesByCountryAndIndustry', query, {
          cache: true,
          cacheKey: query,
        })
        .then((res) => {
          if (res.status === 200) {
            commit('setRelevantCompanies', res.data);
            if (res.data && res.data.length > 0) {
              return res.data.map(c => c.compNumber);
            }

            return null;
          }

          return null;
        })
        .then((companies) => {
          if (companies && companies.length > 0) {
            const indicatorQuery = {
              companies,
            };
            financeAxios.post('/aims/api/finance/indicatorsByCompanies', indicatorQuery, {
                cache: true,
                cacheKey: indicatorQuery,
              })
              .then((res) => {
                if (res.status === 200) {
                  commit('setRelevantCompaniesIndicators', res.data);
                  commit('plotBox', state.indicatorSelected);
                  commit('setChartLoading', false);
                } else {
                  commit('setRelevantCompaniesIndicators', []);
                  commit('unplotBox');
                  commit('setChartLoading', false);
                }
              });
          } else {
            commit('setRelevantCompaniesIndicators', []);
            commit('unplotBox');
            commit('setChartLoading', false);
          }
        });
    },
  },
  getters: {
    sectorsTree(state) {
      const tree = state.sectors.map((sector) => {
        const igChildren = sector.industryGroups.map((ig) => {
          const industryChilren = ig.industries.map((i) => {
            const subIndustryChildren = i.subIndustries.map((si) => {
              const siChild = {
                label: si.name,
                code: si.code,
              };
              return siChild;
            });
            return {
              label: i.name,
              code: i.code,
              children: subIndustryChildren,
            };
          });
          return {
            label: ig.name,
            code: ig.code,
            children: industryChilren,
          };
        });
        return {
          label: sector.name,
          code: sector.code,
          children: igChildren,
        };
      });
      return tree;
    },
  },
};



// WEBPACK FOOTER //
// ./src/store/modules/financeModule.js